import {
  type OfferTemplate,
  phaseOf,
  type PreparedDocumentResponse,
  QUOTE_DOCUMENT_LABELS,
  type QuoteDocumentFormat,
  type QuoteDocumentKind,
  quoteDocumentFileName,
  type QuoteResponse,
} from '@k-management/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { apiDownload, apiRequest } from '../../lib/api.js';

/** The documents of the dialog: the printed ones, and the offer from a Word template. */
type Choice = QuoteDocumentKind | 'modello';

const DOCUMENT_NOTES: Record<QuoteDocumentKind, string> = {
  offerta: 'Per il cliente: ricavi, condizioni e firme.',
  preventivo: 'Per K-Rev: costi, margini, controlli e approvazioni. Non va inviato al cliente.',
};

const FORMATS: { value: QuoteDocumentFormat; label: string; note: string; action: string }[] = [
  { value: 'pdf', label: 'PDF', note: 'Da inviare così com’è.', action: 'Scarica PDF' },
  {
    value: 'docx',
    label: 'Word (.docx)',
    note: 'Da modificare o da unire alla vostra offerta.',
    action: 'Scarica Word',
  },
];

/**
 * «Documenti» of a quote (04/10/2026): which document and in which format. The
 * PDF is printed by the browser from the document page; the Word file is made
 * by the API. «Offerta completa da modello» (05/10/2026) fills a Word template
 * of the folder «Modelli» and files it in the tab «File» of the offer.
 */
export function DocumentsDialog({
  quote,
  canCosts,
  canPrepare,
  onClose,
  onPrepared,
}: {
  quote: QuoteResponse;
  /** Who lacks «Costi e margini» does not see the internal document. */
  canCosts: boolean;
  /** «Documenti delle offerte» and the right to write: the document is filed with the offer. */
  canPrepare: boolean;
  onClose: () => void;
  onPrepared: (prepared: PreparedDocumentResponse) => void;
}) {
  const offer = phaseOf(quote.state) !== 'preventivo';
  const kinds: Choice[] = [
    ...(offer ? (['offerta'] as const) : []),
    ...(offer && canPrepare ? (['modello'] as const) : []),
    ...(canCosts ? (['preventivo'] as const) : []),
  ];
  const [kind, setKind] = useState<Choice | undefined>(kinds[0]);
  const [format, setFormat] = useState<QuoteDocumentFormat>('pdf');
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState('');
  const templates = useQuery({
    queryKey: ['document-templates'],
    queryFn: () => apiRequest<OfferTemplate[]>('/quotes/document-templates'),
    enabled: offer && canPrepare,
  });
  const chosenTemplate = templateId || templates.data?.[0]?.id || '';
  const word = useMutation({
    mutationFn: (chosen: QuoteDocumentKind) =>
      apiDownload(
        `/quotes/${quote.id}/documents/${chosen}/docx`,
        `${quoteDocumentFileName(quote, chosen)}.docx`,
      ),
    onSuccess: onClose,
    onError: (failure: Error) => setError(failure.message),
  });
  const prepare = useMutation({
    mutationFn: () =>
      apiRequest<PreparedDocumentResponse>(`/quotes/${quote.id}/files/from-template`, {
        method: 'POST',
        body: { templateId: chosenTemplate },
      }),
    onSuccess: onPrepared,
    onError: (failure: Error) => setError(failure.message),
  });

  const confirm = () => {
    if (!kind) return onClose();
    setError('');
    if (kind === 'modello') {
      if (chosenTemplate) prepare.mutate();
      else setError('Nella cartella «Modelli» di SharePoint non c’è ancora un modello Word.');
    } else if (format === 'pdf') {
      window.open(`/preventivatore/${quote.id}/documento/${kind}?stampa`, '_blank');
      onClose();
    } else {
      word.mutate(kind);
    }
  };

  return (
    <ConfirmDialog
      title="Documenti"
      confirmLabel={
        !kind
          ? 'Chiudi'
          : kind === 'modello'
            ? 'Prepara il documento'
            : FORMATS.find((item) => item.value === format)!.action
      }
      busy={word.isPending || prepare.isPending}
      error={error}
      onConfirm={confirm}
      onCancel={onClose}
    >
      {kinds.length === 0 ? (
        <p className="muted">
          L’offerta economica nasce con l’approvazione del preventivo; il preventivo interno è per
          chi ha «Costi e margini».
        </p>
      ) : (
        <>
          <fieldset className="choices">
            <legend>Documento</legend>
            {kinds.map((value) => (
              <label key={value} className={kind === value ? 'choice selected' : 'choice'}>
                <input type="radio" checked={kind === value} onChange={() => setKind(value)} />
                <span>
                  <strong>
                    {value === 'modello'
                      ? 'Offerta completa da modello'
                      : QUOTE_DOCUMENT_LABELS[value]}
                  </strong>
                  <small className="muted block">
                    {value === 'modello'
                      ? 'Il vostro modello Word riempito con i dati dell’offerta, salvato nella scheda «File» per completarlo in Word.'
                      : DOCUMENT_NOTES[value]}
                  </small>
                </span>
              </label>
            ))}
          </fieldset>
          {kind === 'modello' ? (
            <fieldset className="choices">
              <legend>Modello</legend>
              {templates.isPending && <p className="muted">Caricamento dei modelli…</p>}
              {templates.isError && <p className="error">{templates.error.message}</p>}
              {templates.data?.length === 0 && (
                <p className="muted">
                  Nella cartella «Modelli» della raccolta SharePoint non c’è ancora un file Word: lo
                  carica un amministratore.
                </p>
              )}
              {templates.data && templates.data.length > 0 && (
                <select
                  aria-label="Modello"
                  value={chosenTemplate}
                  onChange={(event) => setTemplateId(event.target.value)}
                >
                  {templates.data.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              )}
            </fieldset>
          ) : (
            <fieldset className="choices">
              <legend>Formato</legend>
              {FORMATS.map((item) => (
                <label
                  key={item.value}
                  className={format === item.value ? 'choice selected' : 'choice'}
                >
                  <input
                    type="radio"
                    checked={format === item.value}
                    onChange={() => setFormat(item.value)}
                  />
                  <span>
                    <strong>{item.label}</strong>
                    <small className="muted block">{item.note}</small>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        </>
      )}
    </ConfirmDialog>
  );
}
